import React, { useEffect } from 'react'
import { Box, Stack, Tab, TabProps, Tabs, Typography } from '@common/components'
import {
  ChatBubbleOutlineIcon,
  DashboardCustomizeIcon,
  DataUsageIcon,
  FactCheckIcon,
  FeedIcon,
  ImportContactsIcon,
  LyricsIcon,
  RadioButtonCheckedIcon,
} from '@common/icons'
import { RouterLink, useParams, useSearchParams, useNavigate } from '@lib/router'
import {
  FEEDBACK_ROUTE,
  NOTES_ROUTE,
  RECORDINGS_ROUTE,
  RESOURCES_ROUTE,
  STATISTIC_ROUTE,
  SUMMARY_ROUTE,
  TABLE_VIEW_ROUTE,
  TABLE_VIEW_SURVEY_ROUTE,
  USAGE_ROUTE,
} from '@common/constants/routes'
import { useUser } from '@features/auth/hooks'
import StatisticIcon from '../../icons/statistic-icon'
import TabsIcon from '../../icons/tabs-icon'
import { useAppSelector } from '@common/hooks'
import { selectActiveRole } from '@features/auth/auth-slice'

const DashboardTabs = (props: { value: number | string }) => {
  const { value } = props
  const { feedback_id } = useParams()
  const [searchParams] = useSearchParams()
  const { user, isAdmin } = useUser()
  const activeRole = useAppSelector(selectActiveRole)
  const navigate = useNavigate()

  const studentTabs = [
    {
      value: RECORDINGS_ROUTE,
      label: 'Recording',
      icon: RadioButtonCheckedIcon,
    },
    {
      value:
        (location.pathname.startsWith(FEEDBACK_ROUTE) && feedback_id) ||
        (location.pathname.startsWith(SUMMARY_ROUTE) && feedback_id)
          ? SUMMARY_ROUTE + `/${feedback_id}`
          : SUMMARY_ROUTE,
      label: 'Summary',
      icon: DashboardCustomizeIcon,
      id: 'summary-tab-button',
    },
    {
      value:
        (location.pathname.startsWith(FEEDBACK_ROUTE) && feedback_id) ||
        (location.pathname.startsWith(SUMMARY_ROUTE) && feedback_id)
          ? FEEDBACK_ROUTE + `/${feedback_id}`
          : FEEDBACK_ROUTE,
      label: 'Feedback',
      icon: LyricsIcon,
      id: 'feedback-tab-button',
    },
    {
      value: RESOURCES_ROUTE,
      label: 'Resources',
      icon: ImportContactsIcon,
    },
  ]

  const teacherTabs = [
    // { value: NOTES_ROUTE, label: 'Speaking Tasks', icon: FeedIcon, id: 'speaking-tasks' },
    {
      value:
        (location.pathname.startsWith(FEEDBACK_ROUTE) && feedback_id) ||
        (location.pathname.startsWith(SUMMARY_ROUTE) && feedback_id)
          ? FEEDBACK_ROUTE + `/${feedback_id}`
          : FEEDBACK_ROUTE,
      label: 'Feedback',
      icon: LyricsIcon,
    },
    {
      value:
        (location.pathname.startsWith(FEEDBACK_ROUTE) && feedback_id) ||
        (location.pathname.startsWith(SUMMARY_ROUTE) && feedback_id)
          ? SUMMARY_ROUTE + `/${feedback_id}`
          : SUMMARY_ROUTE,
      label: 'Test Result',
      icon: FactCheckIcon,
      id: 'summary-tab-button',
    },
    {
      value: TABLE_VIEW_SURVEY_ROUTE,
      label: 'Survey',
      icon: ChatBubbleOutlineIcon,
      id: 'table-view-survey-tab-button',
    },
  ]

  const adminTabs = [
    {
      value: USAGE_ROUTE,
      label: 'USAGE',
      icon: DataUsageIcon,
      id: 'usage-view',
      preserveParams: true,
    },
    {
      value: STATISTIC_ROUTE,
      label: 'PERFORMANCE',
      icon: StatisticIcon,
      id: 'statiscs-view',
      iconCondition: location.pathname.startsWith(STATISTIC_ROUTE) ? '#000' : '#747474',
      preserveParams: true,
    },
    {
      value: TABLE_VIEW_ROUTE,
      label: 'TABLE VIEW',
      icon: TabsIcon,
      id: 'table-view',
      iconCondition: location.pathname.startsWith(TABLE_VIEW_ROUTE) ? '#000' : '#747474',
      preserveParams: true,
    },
    {
      value: TABLE_VIEW_SURVEY_ROUTE,
      label: 'Survey',
      icon: ChatBubbleOutlineIcon,
      id: 'table-view-survey-tab-button',
    },
  ]

  let arrayTabs: any[] = []
  const hasSuperAdminRole = isAdmin

  if (hasSuperAdminRole || activeRole === 'admin') {
    arrayTabs = adminTabs
  } else if (activeRole !== 'teacher' && user?.status === 'STUDENT') {
    arrayTabs = studentTabs
  } else if (activeRole === 'teacher' || user?.status === 'TEACHER') {
    arrayTabs = teacherTabs
  }

  return (
    <>
      <Tabs
        variant='scrollable'
        scrollButtons='auto'
        allowScrollButtonsMobile
        aria-label='scrollable auto tabs'
        sx={{
          '& .MuiTabScrollButton-root': {
            minHeight: '0px',
            '&.Mui-disabled': {
              display: 'none',
            },
          },
        }}
      >
        {arrayTabs.map((elem, index) => {
          const Icon = elem.icon
          // Only preserve search params for admin tabs
          const to =
            elem.preserveParams && searchParams.toString()
              ? `${elem.value}?${searchParams.toString()}`
              : elem.value

          return (
            <CustomTab
              active={elem.value === value}
              key={index}
              icon={<Icon sx={{ mb: 0 }} color={elem.value === value ? '#000' : '#555'} />}
              component={RouterLink}
              to={to}
              label={<Typography variant='body2'>{elem.label}</Typography>}
              value={elem.value}
              id={elem.id}
            />
          )
        })}
      </Tabs>
    </>
  )
}

const CustomTab = (props: TabProps & { active: boolean; to?: string }) => {
  const { icon, label, active } = props
  return (
    <Tab
      {...props}
      sx={{
        px: 2,
        backgroundColor: active ? '#FFF' : '#F0F0F0',
        opacity: active ? 1 : 0.9,
        color: active ? '#000' : '#555',
        '&.MuiTab-root': {
          flexDirection: 'row',
          alignItems: 'center',
          minHeight: '0px',
          marginRight: '10px',
          height: '50px',
          '& svg': {
            transform: 'scale(0.8)',
            marginBottom: '0px',
          },
        },
      }}
    />
  )
}

export default DashboardTabs
